"use client"

import { useState, useEffect } from "react"
import Slider from "react-slick"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../../firebase"
import { FaSpinner, FaExclamationTriangle } from "react-icons/fa"

const Hero = ({ handleOrderPopup }) => {
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchImages = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log("Hero: Fetching images from Firebase...")
      const snapshot = await getDocs(collection(db, "images"))
      console.log("Hero: Images snapshot size:", snapshot.size)

      const imageData = snapshot.docs
        .map((doc) => {
          const data = doc.data()
          console.log("Hero: Image doc:", doc.id, data)
          return data
        })
        .filter((img) => img.url && img.url.trim() !== "")

      console.log("Hero: Filtered images:", imageData)
      setImages(imageData)

      if (imageData.length === 0) {
        setError("No images found in the collection")
      }
    } catch (err) {
      console.error("Hero: Failed to load images:", err)
      setError(`Failed to load images: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchImages()
  }, [])

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 1200,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    cssEase: "cubic-bezier(0.4, 0, 0.2, 1)",
    pauseOnHover: false,
    pauseOnFocus: false,
    fade: true,
    adaptiveHeight: false,
  }

  return (
    <div className="relative overflow-hidden min-h-[550px] sm:min-h-[650px] bg-gradient-to-br from-gray-100 via-blue-50/30 to-green-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-800 flex justify-center items-center duration-200">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-green-400/10 rounded-full mix-blend-multiply filter blur-3xl animate-float"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-green-400/10 to-blue-400/10 rounded-full mix-blend-multiply filter blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-purple-400/5 to-pink-400/5 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"></div>
      </div>

      {/* Background Pattern */}
      <div className="h-[700px] w-[700px] bg-primary/20 absolute -top-1/2 right-0 rounded-3xl rotate-45 -z-10 opacity-30"></div>

      {/* Hero Content */}
      <div className="container pb-8 sm:pb-0 relative z-10">
        {loading ? (
          <div className="text-center py-20">
            <div className="relative inline-block mb-8">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-green-600 rounded-full blur-lg opacity-30 animate-pulse"></div>
              <FaSpinner className="relative w-16 h-16 text-blue-600 animate-spin" />
            </div>
            <p className="text-xl text-gray-600 dark:text-gray-400 font-medium">Loading image gallery...</p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Fetching beautiful moments...</p>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 max-w-md mx-auto">
              <FaExclamationTriangle className="w-16 h-16 text-red-600 dark:text-red-400 mx-auto mb-6" />
              <h3 className="text-2xl font-semibold text-red-800 dark:text-red-200 mb-4">Image Loading Error</h3>
              <p className="text-red-700 dark:text-red-300 mb-6">{error}</p>
              <button
                onClick={fetchImages}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-20">
            <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-2xl p-8 max-w-md mx-auto backdrop-blur-sm">
              <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">No Images Available</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Image gallery will be displayed here once images are uploaded.
              </p>
              <button
                onClick={fetchImages}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-all duration-200"
              >
                Refresh Gallery
              </button>
            </div>
          </div>
        ) : (
          <div className="hero-slider-container">
            <Slider {...sliderSettings}>
              {images.map((data, index) => (
                <div key={index} className="pt-8">
                  <div className="flex justify-center items-center h-full">
                    <div
                      data-aos="zoom-in"
                      data-aos-once="true"
                      className="relative z-10 rounded-3xl overflow-hidden shadow-2xl group"
                      style={{ maxWidth: "70%", maxHeight: "500px" }}
                    >
                      {/* Image Container with Overlay */}
                      <div className="relative overflow-hidden">
                        <img
                          src={data.url || "/placeholder.svg"}
                          alt={data.title || `Gallery Image ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          onError={(e) => {
                            console.error("Hero: Image failed to load:", data.url)
                            e.target.src = "/placeholder.svg"
                          }}
                          onLoad={() => console.log("Hero: Image loaded successfully:", data.url)}
                        />

                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                        {/* Image Title Overlay */}
                        {data.title && (
                          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                            <h3 className="text-white text-xl font-bold mb-2">{data.title}</h3>
                            {data.description && <p className="text-white/90 text-sm">{data.description}</p>}
                          </div>
                        )}
                      </div>

                      {/* Decorative Border */}
                      <div className="absolute inset-0 rounded-3xl border-4 border-white/20 pointer-events-none"></div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(3deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-3deg); }
        }
        
        .animate-float { animation: float 8s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 10s ease-in-out infinite; }

        .hero-slider-container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
        }

        .hero-slider-container .slick-slide {
          outline: none;
        }

        .hero-slider-container .slick-slide > div {
          height: 100%;
        }

        .hero-slider-container .slick-track {
          display: flex;
          align-items: center;
        }

        .hero-slider-container .slick-slide {
          height: auto;
        }

        /* Custom fade transition */
        .hero-slider-container .slick-fade .slick-slide {
          transition: opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </div>
  )
}

export default Hero
